import React, { useRef, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { siteSettings } from '@settings/site-settings';
import { useUI } from '@contexts/ui.context';
import { ROUTES } from '@utils/routes';
import { addActiveScroll } from '@utils/add-active-scroll';
import SearchIcon from '@components/icons/search-icon';
import HeaderMenu from '@components/layout/header/header-menu';
import Logo from '@components/ui-old/logo';
import LanguageSwitcher from '@components/ui-old/language-switcher';
import { useUserProfileQuery } from '@framework/customer/use-update-customer';
import { useNewsQuery } from '@framework/news/get-news';
import { transformMenu } from '@/hooks/final-menu';
import RegionSwitcher from '@components/other/region-switcher';
import { getCurrentLocationAndLanguage } from '@utils/location';
import { ModeToggle } from '@components/other/theme-switcher';
const AuthMenu = dynamic(() => import('./auth-menu'), {
  ssr: false
});
const CartButton = dynamic(() => import('@components/cart/cart-button'), {
  ssr: false
});
type DivElementRef = React.MutableRefObject<HTMLDivElement>;
const {
  site_header
} = siteSettings;
const Header: React.FC = () => {
  const {
    openSearch,
    openModal,
    setModalView,
    isAuthorized
  } = useUI();
  const {
    location
  } = getCurrentLocationAndLanguage();
  const {
    data: user,
    isLoading
  } = useUserProfileQuery();
  const {
    t
  } = useTranslation('common');
  const {
    data: newsData
  } = useNewsQuery();
  const hasNews = !!newsData?.results?.length;
  const siteHeaderRef = useRef() as DivElementRef;
  addActiveScroll(siteHeaderRef);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  function handleLogin() {
    setModalView('LOGIN_VIEW');
    return openModal();
  }
  const baseMenu = site_header.menu;
  const finalMenu = transformMenu(baseMenu, {
    isAuthorized: isMounted ? isAuthorized : false,
    hasNews,
    location
  });
  return <header id='siteHeader' ref={siteHeaderRef} className='relative z-20 w-full h-16 sm:h-20 lg:h-24' data-sentry-component="Header" data-sentry-source-file="header.tsx">
      <div className='fixed z-20 w-full h-16 px-4 text-neutral-700 dark:text-neutral-200 transition duration-200 ease-in-out bg-white dark:bg-zinc-950 dark:bg-zinc-950 innerSticky body-font sm:h-20 lg:h-24 md:px-8 lg:px-6'>
        <div className='flex items-center justify-center mx-auto max-w-[1920px] h-full w-full'>
          <Logo data-sentry-element="Logo" data-sentry-source-file="header.tsx" />

          <HeaderMenu data={finalMenu} className="hidden lg:flex ltr:md:ml-6 rtl:md:mr-6 ltr:xl:ml-10 rtl:xl:mr-10" data-sentry-element="HeaderMenu" data-sentry-source-file="header.tsx" />

          <div className="flex items-center space-x-4 ltr:ml-auto rtl:mr-auto ltr:lg:mr-5 rtl:lg:ml-5 ltr:xl:mr-8 rtl:xl:ml-8 ltr:2xl:mr-10 rtl:2xl:ml-10">
            {location === "kz" && <RegionSwitcher />}
            <LanguageSwitcher data-sentry-element="LanguageSwitcher" data-sentry-source-file="header.tsx" />
            <div className="hidden lg:flex"><ModeToggle data-sentry-element="ModeToggle" data-sentry-source-file="header.tsx" /></div>
          </div>
          <div className='items-center justify-end flex-shrink-0 hidden lg:flex gap-x-6 lg:gap-x-5 xl:gap-x-8 2xl:gap-x-10 ltr:ml-auto rtl:mr-auto'>
            <button className='relative flex items-center justify-center flex-shrink-0 h-auto transform focus:outline-none' onClick={openSearch} aria-label='search-button'>
              <SearchIcon data-sentry-element="SearchIcon" data-sentry-source-file="header.tsx" />
            </button>
            <div className='-mt-0.5 flex-shrink-0'>
              <AuthMenu isAuthorized={isAuthorized} href={ROUTES.ACCOUNT} loading={isLoading} className="text-sm font-semibold xl:text-base text-heading dark:text-neutral-200 flex items-center space-x-2" btnProps={{
              className: 'text-sm xl:text-base text-heading dark:text-neutral-200 font-semibold focus:outline-none',
              // @ts-ignore
              children: t('text-sign-in'),
              onClick: handleLogin
            }} user={{
              avatar: user?.avatar,
              firstName: user?.firstName,
              lastName: user?.lastName,
              level: user?.level
            }} data-sentry-element="AuthMenu" data-sentry-source-file="header.tsx" />
            </div>
            <CartButton data-sentry-element="CartButton" data-sentry-source-file="header.tsx" />
          </div>
        </div>
      </div>
    </header>;
};
export default Header;